import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const Landing: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLUListElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        mobileMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target as Node)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <div className={`mobile-menu-backdrop ${mobileMenuOpen ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}></div>
      <header style={{ background: 'rgba(10,10,10,0.65)', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <nav className="container">
          <button className="logo" onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>VirtualFit</button>
          <ul ref={mobileMenuRef} className={`nav-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <li><a href="#features" onClick={() => setMobileMenuOpen(false)}>Features</a></li>
            <li><a href="#integration" onClick={() => setMobileMenuOpen(false)}>Integration</a></li>
            <li><a href="#testimonials" onClick={() => setMobileMenuOpen(false)}>Testimonials</a></li>
            <li><a href="#pricing" onClick={() => setMobileMenuOpen(false)}>Pricing</a></li>
            <li><a href="/demo-store" onClick={() => setMobileMenuOpen(false)}>Demo Store</a></li>
          </ul>
          {!isLoggedIn ? (
            <>
              <button className="cta-button" onClick={() => navigate('/login')}>Login</button>
              <button ref={hamburgerRef} className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
              </button>
            </>
          ) : (
            <>
              <button className="cta-button" onClick={() => navigate('/dashboard')}>Dashboard</button>
              <button ref={hamburgerRef} className="mobile-menu-toggle" onClick={toggleMobileMenu}>
                <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}></span>
              </button>
            </>
          )}
        </nav>
      </header>
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1>Transform E-commerce with <span className="highlight">AI-Powered</span> Virtual Try-On</h1>
              <p>Integrate our cutting-edge virtual try-on technology in minutes and boost your conversions. The future of online shopping is here.</p>
              <div className="hero-buttons">
                <button className="btn-primary" onClick={() => navigate('/register')}>🚀 Get Started Free</button>
                <a href="/demo-store" className="btn-secondary">📱 View Demo</a>
              </div>
            </div>
            <div className="hero-visual">
              <div className="demo-container">
                <div className="phone-3d">
                  <div className="phone-frame">
                    <div className="phone-screen">
                      {/* Show widget preview image on mobile */}
                      <img
                        src="/assets/frontend_img.png"
                        alt="Widget preview with fake product"
                        className="widget-preview-img"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  </div>
                </div>
                <div className="particles">
                  <div className="particle" style={{ left: '10%', animationDelay: '0s' }}></div>
                  <div className="particle" style={{ left: '20%', animationDelay: '2s' }}></div>
                  <div className="particle" style={{ left: '30%', animationDelay: '4s' }}></div>
                  <div className="particle" style={{ left: '40%', animationDelay: '6s' }}></div>
                  <div className="particle" style={{ left: '50%', animationDelay: '1s' }}></div>
                  <div className="particle" style={{ left: '60%', animationDelay: '3s' }}></div>
                  <div className="particle" style={{ left: '70%', animationDelay: '5s' }}></div>
                  <div className="particle" style={{ left: '80%', animationDelay: '7s' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="features" id="features">
        <div className="container">
          <h2 className="section-title">Why Choose VirtualFit?</h2>
          <div className="bento-grid">
            <div className="bento-card">
              <div className="feature-icon">⚡</div>
              <h3>Lightning Fast Integration</h3>
              <p>Deploy our virtual try-on solution in under 5 minutes with a single line of code. Compatible with all major e-commerce platforms including Shopify, WooCommerce, and custom builds.</p>
            </div>
            <div className="bento-card">
              <div className="feature-icon">🔒</div>
              <h3>Privacy First</h3>
              <p>Your photos are <b>never stored</b>. All try-ons are processed securely and privately, ensuring your data stays yours—always.</p>
            </div>
            <div className="bento-card">
              <div className="feature-icon">📱</div>
              <h3>Multi-Platform</h3>
              <p>Works seamlessly across mobile, tablet, and desktop devices with responsive design.</p>
            </div>
            <div className="bento-card">
              <div className="feature-icon">🧠</div>
              <h3>Advanced AI</h3>
              <p>Powered by cutting-edge machine learning algorithms that understand body types, clothing physics, and lighting conditions for the most realistic virtual try-on experience.</p>
            </div>
            <div className="bento-card">
              <div className="feature-icon">🔧</div>
              <h3>Fully Customizable</h3>
              <p>Adapt the interface to match your brand identity with custom themes, colors, and layouts.</p>
            </div>
            <div className="bento-card">
              <div className="feature-icon">📊</div>
              <h3>Real-time Analytics</h3>
              <p>Track engagement metrics and conversion rates with our comprehensive dashboard.</p>
            </div>
          </div>
        </div>
      </section>
      <section className="integration" id="integration">
        <div className="container">
          <div className="integration-content">
            <div className="integration-text">
              <h2>Seamless Integration in 3 Steps</h2>
              <p>Our solution integrates perfectly with your existing infrastructure without requiring major code changes or technical expertise.</p>
              <ul className="integration-steps">
                <li><span className="step-number">1</span>Add our lightweight SDK to your product pages</li>
                <li><span className="step-number">2</span>Configure your product catalog via our plateform</li>
                <li><span className="step-number">3</span>Enable virtual try-on and watch conversions soar</li>
              </ul>
              <a href="/docs" className="btn-primary">View Full Documentation</a>
            </div>
            <div className="code-terminal">
  <div className="terminal-header">
    <div className="terminal-dot red"></div>
    <div className="terminal-dot yellow"></div>
    <div className="terminal-dot green"></div>
  </div>
  <div className="code-preview" style={{ position: 'relative' }}>
    <pre style={{
      background: 'none',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: 13,
      margin: 0,
      padding: 0,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-all',
      border: 'none',
      boxShadow: 'none',
    }}>{`<!-- Virtual Try-On Widget Embed -->
<div id="try-on-widget"></div>
<script src="https://virtualfit.puniminds.com/virtual-tryon-widget.min.js"></script>
<script>
  VirtualTryOnWidget.init({
    apiKey: 'YOUR_API_KEY',//pragma: allowlist secret
    productId: 'PRODUCT_ID',
  });
</script>`}</pre>
    <button
      style={{
        position: 'absolute',
        top: 12,
        right: 12,
        background: '#4a5568',
        color: 'white',
        border: 'none',
        borderRadius: 4,
        padding: '0.2rem 0.6rem',
        fontSize: '0.8rem',
        cursor: 'pointer',
        opacity: 0.85,
      }}
      onClick={() => {
        navigator.clipboard.writeText(`<!-- Virtual Try-On Widget Embed -->\n<div id=\"try-on-widget\"></div>\n<script src=\"https://virtualfit.puniminds.com/virtual-tryon-widget.min.js\"></script>\n<script>\n  VirtualTryOnWidget.init({\n    apiKey: 'YOUR_API_KEY',\n    productId: 'PRODUCT_ID',\n    // ...other config\n  });\n<\/script>`);
      }}
    >Copy</button>
  </div>
</div>
          </div>
        </div>
      </section>
      {/* Pricing Plans Section */}
      <section className="pricing" id="pricing" style={{ background: 'rgba(255,255,255,0.03)', padding: '64px 0 32px 0', margin: '0 0 48px 0' }}>
        <div className="container">
          <h2 style={{ color: 'white', fontWeight: 700, fontSize: 32, marginBottom: 12, textAlign: 'center' }}>Pricing Plans</h2>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 16, marginBottom: 36, textAlign: 'center' }}>
            Simple, transparent pricing. No hidden fees. Choose the plan that fits your needs.
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 24,
            justifyContent: 'center',
            alignItems: 'stretch',
            margin: '0 auto',
            maxWidth: 1100
          }}>
            {[{
              id: 'starter',
              name: 'Starter',
              credits: 100,
              price: 29,
              features: ['100 AI Credits', 'Basic Support', 'Standard Processing', 'Analytics Dashboard']
            }, {
              id: 'pro',
              name: 'Professional',
              credits: 500,
              price: 99,
              originalPrice: 145,
              popular: true,
              features: ['500 AI Credits', 'Priority Support', 'Fast Processing', 'Analytics Dashboard']
            }, {
              id: 'enterprise',
              name: 'Enterprise',
              credits: 2000,
              price: 299,
              originalPrice: 580,
              features: ['2000 AI Credits', '24/7 Support', 'Ultra Fast Processing', 'Analytics Dashboard', 'Custom Integration']
            }].map(pkg => (
              <div
                key={pkg.id}
                style={{
                  background: 'rgba(255,255,255,0.10)',
                  borderRadius: 20,
                  padding: 28,
                  border: pkg.popular ? '2px solid #4ecdc4' : '1.5px solid rgba(255,255,255,0.18)',
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'stretch',
                  minHeight: 420
                }}
              >
                {pkg.popular && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#4ecdc4',
                    color: 'white',
                    padding: '4px 16px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 2,
                  }}>
                    MOST POPULAR
                  </div>
                )}
                {pkg.originalPrice && (
                  <div style={{
                    position: 'absolute',
                    top: -12,
                    right: 12,
                    background: '#ffd93d',
                    color: '#333',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    zIndex: 2,
                  }}>
                    Save {Math.round(100 - (pkg.price / pkg.originalPrice) * 100)}%
                  </div>
                )}
                <h4 style={{ color: 'white', marginBottom: 8, fontSize: 20, fontWeight: 600, textAlign: 'center' }}>
                  {pkg.name}
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 32, fontWeight: 700, color: '#4ecdc4' }}>
                    €{pkg.price}
                  </span>
                  {pkg.originalPrice && (
                    <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', marginLeft: 8 }}>
                      €{pkg.originalPrice}
                    </span>
                  )}
                </div>
                <div style={{ marginTop: 12, marginBottom: 12 }}>
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    {pkg.features.map((feature, index) => (
                      <li key={index} style={{ color: '#4ecdc4', fontWeight: 500, fontSize: 14, marginBottom: 4, listStyle: 'none', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: 8 }}>✔️</span> <span style={{ color: 'white' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ marginTop: 'auto' }}>
                  <div style={{ color: '#ffd93d', fontSize: 13, fontWeight: 600, marginBottom: 8, textAlign: 'center' }}>
                    Good for up to {pkg.credits} generations
                  </div>
                  <button
                    onClick={() => window.location.href = `/register?plan=${pkg.id}`}
                    style={{
                      width: '100%',
                      background: pkg.popular ? '#4ecdc4' : 'rgba(255,255,255,0.1)',
                      color: pkg.popular ? 'white' : 'white',
                      border: 'none',
                      padding: '16px',
                      borderRadius: 12,
                      fontSize: 16,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    Choose Plan
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="testimonials" id="testimonials">
        <div className="container">
          <h2 className="section-title">Trusted by Industry Leaders</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                "VirtualFit transformed our online store. We saw a 45% increase in conversions within the first month and returns dropped by 60%. The integration was seamless and their support team is exceptional."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">SM</div>
                <div className="author-info">
                  <h4>Sarah Mitchell</h4>
                  <p>CEO, FashionForward</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                "The virtual try-on feature has become our customers' favorite tool. It's incredibly accurate and the setup took less than 10 minutes. Our mobile conversions have never been higher."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">JC</div>
                <div className="author-info">
                  <h4>James Chen</h4>
                  <p>CTO, StyleHub</p>
                </div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-content">
                "We integrated VirtualFit across all our brands and the results speak for themselves. Customer satisfaction is up 35% and we're saving thousands on returns processing."
              </div>
              <div className="testimonial-author">
                <div className="author-avatar">AL</div>
                <div className="author-info">
                  <h4>Anna Lopez</h4>
                  <p>VP Digital, RetailMax</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <footer>
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>VirtualFit</h3>
              <p>The next-generation virtual try-on solution for modern e-commerce.</p>
            </div>
            <div className="footer-section">
              <h3>Product</h3>
              <a href="#">Features</a>
              <a href="#">Integration</a>
              <a href="#">API Documentation</a>
              <a href="#">SDKs</a>
            </div>
            <div className="footer-section">
              <h3>Company</h3>
              <a href="#">About Us</a>
              <a href="#">Blog</a>
              <a href="#">Careers</a>
              <a href="#">Press</a>
            </div>
            <div className="footer-section">
              <h3>Support</h3>
              <a href="#">Help Center</a>
              <a href="#">Contact</a>
              <a href="#">Status</a>
              <a href="#">Community</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 VirtualFit. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Landing;
